import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
  ViewContainerRef,
  WritableSignal,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { I18nState } from '@app/core/states/i18n.state';
import { RadioState } from '@app/core/states/radio.state';
import { aLogEvent, AMPLITUDE_EVENT } from '@app/shared/amplitude-events';
import { FullCalendarComponent } from '@fullcalendar/angular';
import {
  Calendar,
  CalendarOptions,
  DayHeaderContentArg,
  EventApi,
  EventClickArg,
} from '@fullcalendar/core';
import { EventImpl, VerboseFormattingArg } from '@fullcalendar/core/internal';
import frLocale from '@fullcalendar/core/locales/fr';
import interactionPlugin from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import timeGridPlugin from '@fullcalendar/timegrid';
import { ResizeObserver } from '@juggle/resize-observer';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Select, Store } from '@ngxs/store';
import { BreakListBreak, breakToasts } from '@radioking/break';
import { SchedulerTrack } from '@radioking/scheduler-core';
import { ToasterService } from '@radioking/shared/common-services';
import { SugestionInput } from '@radioking/ui/input';
import { throttle } from 'lodash';
import moment from 'moment-timezone';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, pairwise, startWith } from 'rxjs/operators';
import { PlanningDayHeaderComponent } from '../../../../../planning/src/lib/components/planning-day-header/planning-day-header.component';

import { MOBILE_DIALOG_CONFIG } from '@app/shared/constants';
import { DatesRange } from '@radioking/planning';
import { ProgramsRequest } from '../../../../../programs/src/lib/states/programs.actions';
import { ProgramsState } from '../../../../../programs/src/lib/states/programs.state';
import {
  SchedulerAllPlaylistsRequest,
  SchedulerAllTracksRequest,
} from '../../../../../scheduler-core/src/lib/states/scheduler-core.actions';
import { SchedulerCoreState } from '../../../../../scheduler-core/src/lib/states/scheduler-core.state';
import { CalendarDatesChanged } from '../../models/break.model';
import { DateSelectDatesChanged } from '../../models/date-select.model';
import {
  GetBreakListRequest,
  ResetBreakState,
  SetCurrentDay,
  TooltipNoContent,
} from '../../states/break.actions';
import { BreakState } from '../../states/break.state';
import { BreakDeletionComponent } from '../break-deletion/break-deletion.component';
import { BreakEditionComponent } from '../break-edition/break-edition.component';
import { BreakPopoverComponent } from '../break-popover/break-popover.component';

const SLOT_DURATION_IN_MINUTES = 15;

@Component({
  selector: 'rk-break-shell',
  templateUrl: './break-shell.component.html',
  styleUrls: ['./break-shell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BreakShellComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild(MatMenuTrigger) menuTrigger: MatMenuTrigger | undefined;

  @Select(RadioState.currentRadioId)
  radioId$: Observable<number>;

  @Select(RadioState.currentRadioTimezone)
  radioTz$: Observable<string>;

  @Select(ProgramsState.allProgramsForDropdown)
  allPrograms$: Observable<SugestionInput[]>;

  @Select(SchedulerCoreState.allPlaylistsForDropdown)
  allPlaylists$: Observable<SugestionInput[]>;

  @Select(BreakState.selectedBreaksCount)
  selectedBreaks$: Observable<EventImpl[]>;

  @Select(SchedulerCoreState.hasNotEmptyPlaylists)
  hasPlaylists$: Observable<boolean>;

  @Select(ProgramsState.hasNotEmptyPrograms)
  hasPrograms$: Observable<boolean>;

  @Select(SchedulerCoreState.allTracks)
  tracks$: Observable<SchedulerTrack[]>;

  @Select(BreakState.isRenderingBreakList)
  isRendering$: Observable<boolean>;

  @Select(BreakState.isFetchingBreakList)
  isLoading$: Observable<boolean>;

  @Select(BreakState.breaks)
  breaks$: Observable<BreakListBreak[]>;

  @Select(BreakState.datesRange)
  datesRange$: Observable<DatesRange>;

  @Select(BreakState.currentDay)
  currentDay$: Observable<moment.Moment>;

  @Output()
  readonly calendarDatesChanged = new EventEmitter<CalendarDatesChanged>(true);

  $tinyHeader: WritableSignal<boolean> = signal(false);

  /**
   * Fullcalendar options
   */
  readonly calendarOptions: Readonly<CalendarOptions> = {
    plugins: [interactionPlugin, timeGridPlugin, momentTimezonePlugin],
    headerToolbar: false,
    initialView: 'timeGridWeek',
    navLinks: false, // Could enhance UX when set to true (development required to sync view selector)
    allDaySlot: false,
    locales: [frLocale],
    eventStartEditable: false,
    eventDurationEditable: false,
    slotDuration: {
      minutes: SLOT_DURATION_IN_MINUTES,
    },
    slotLabelInterval: {
      minutes: SLOT_DURATION_IN_MINUTES,
    },
    slotLabelFormat: this.formatSlotLabel.bind(this),
    dayHeaderContent: this.formatDayHeader.bind(this),
    dayHeaderFormat: {
      weekday: 'short',
      month: 'numeric',
      day: 'numeric',
      omitCommas: true,
    },
    scrollTime: null, // Always preserve current scroll
    nowIndicator: true,
    businessHours: false,
    // initialEvents, // TODO Alternatively, use the `events` setting to fetch from a feed
    firstDay: 1, // First day of week = monday
    weekends: true,
    selectMirror: true,
    datesSet: this.emitDatesChanged.bind(this),
  };

  @ViewChild('calendar') calendarComponent: FullCalendarComponent;

  /**
   * Update size of calendar (rendeing) when resized
   */
  calendarResizeObserver = new ResizeObserver(
    throttle(() => this.calendarUpdateSize(), 250),
  );

  /**
   * Whether scrolling of calendar should be prevented or not
   *
   * E.g.: When a dialog is opened, scrolling should be prevented
   */
  shouldPreventScrolling = false;

  /**
   * Additional breaks when slot has more than one break
   */
  slotMoreBreaks: EventApi[];

  /**
   * Popover's dialog reference when opened
   */
  popoverDialogRef: MatDialogRef<BreakPopoverComponent>;

  /**
   * Useful fot the loading state
   */
  loadedEvents = 0;
  displayedEvents = 0;

  currentTz: string;

  multiBreakClickInfo: EventClickArg;

  @Select(I18nState.currentLang)
  currentLang$: Observable<string>;

  subscription = new Subscription();

  @ViewChild('dayHeaderContentContainer', { read: ViewContainerRef })
  dayHeaderContentContainer: ViewContainerRef;

  dayHeaderComponents: ComponentRef<PlanningDayHeaderComponent>[] = [];

  constructor(
    private readonly dialog: MatDialog,
    private readonly toaster: ToasterService,
    private readonly componentFactory: ComponentFactoryResolver,
    private readonly store: Store,
  ) {}

  /**
   * Get calendar plugin's api (fullcalendar)
   */
  get calendarApi(): Calendar {
    return this.calendarComponent.getApi();
  }

  ngOnInit(): void {
    this.toaster.registerToasts(breakToasts);
    this.listenToRadioSwitch();
  }

  ngAfterViewInit(): void {
    this.subscription.add(this.currentLang$.subscribe(locale => this.setLocale(locale)));
    this.calendarResizeObserver.observe(this.calendarComponent.getApi().el);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.calendarResizeObserver.disconnect();
    this.closeAllDialogs();
    this.resetState();
  }

  listenToRadioSwitch(): void {
    this.subscription.add(
      this.radioId$
        .pipe(
          filter(id => !!id),
          distinctUntilChanged(),
        )
        .subscribe(id => {
          this.getPrograms();
          this.getPlaylists(id);
          this.getTracks(id);
        }),
    );

    this.subscription.add(
      combineLatest([
        this.radioId$.pipe(
          filter(id => !!id),
          distinctUntilChanged(),
        ),
        this.radioTz$.pipe(
          filter(tz => !!tz),
          distinctUntilChanged(),
        ),
        this.calendarDatesChanged.pipe(
          startWith(null),
          pairwise(),
          filter(
            ([prev, curr]) =>
              prev === null || prev.formattedRange !== curr.formattedRange,
          ),
          map(([prev, curr]) => curr),
        ),
      ]).subscribe(([, tz, dates]) => {
        return this.getBreakList(
          moment(dates.start).tz(tz, true),
          moment(dates.end).tz(tz, true),
        );
      }),
    );
  }

  /**
   * Set calendar's locale
   */
  setLocale(locale: string): void {
    this.calendarApi.setOption('locale', locale);
    if (['fr', 'fr-FR'].indexOf(locale) > -1) {
      const r: HTMLStyleElement = document.querySelector(':root');
      r.style.setProperty('--break-hour-column', '60px');
    }
  }

  /**
   * Selected date changed
   */
  dateSelectDatesChanged(event: DateSelectDatesChanged): void {
    this.calendarApi.gotoDate(event.start);
  }

  /**
   * Go to calendar's previous period
   */
  calendarPrevious(): void {
    this.calendarApi.prev();
  }

  /**
   * Go to calendar's next period
   */
  calendarNext(): void {
    this.calendarApi.next();
  }

  /**
   * Go to calendar's today period
   */
  calendarToday(): void {
    const now = moment();
    const calendarDate = this.calendarApi.getDate();

    // When going to today in week view, already being on today's week,
    // but calendar's date is not today: fullcalendar will not trigger "datesSet" event
    const shouldEmitDatesChanged =
      now.isSame(calendarDate, 'week') && !now.isSame(calendarDate, 'day');

    this.calendarApi.today();

    if (shouldEmitDatesChanged) {
      this.emitDatesChanged();
    }
  }

  /**
   * Update size of calendar (rendering) automatically
   */
  calendarUpdateSize(): void {
    this.calendarApi.updateSize();
  }

  /**
   * Format a slot label of calendar (slot interval's time)
   */
  formatSlotLabel(arg: VerboseFormattingArg): string {
    return moment(arg.date).format('LT');
  }

  /**
   * Format a day header of calendar (one cell per day)
   */
  formatDayHeader(arg: DayHeaderContentArg): { domNodes: HTMLElement[] } {
    // Initialize day header components
    if (!this.dayHeaderComponents.length) {
      const componentFactory = this.componentFactory.resolveComponentFactory(
        PlanningDayHeaderComponent,
      );

      for (let i = 1; i <= 7; i++) {
        this.dayHeaderComponents.push(
          this.dayHeaderContentContainer.createComponent(componentFactory),
        );
      }
    }

    const component = this.dayHeaderComponents[arg.dow].instance;
    component.dayHeaderContentArg$.next(arg);

    return {
      domNodes: [component.getElement()],
    };
  }

  /**
   * Dates of calendar changed (changing view, going to previous/next period, etc)
   */
  emitDatesChanged(): void {
    const current = this.calendarApi.getDate();
    const start = this.calendarApi.view.activeStart;
    const end = this.calendarApi.view.activeEnd;
    const formatOptions = {
      isEndExclusive: true,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    };

    const formattedRange = this.calendarApi.formatRange(start, end, formatOptions);
    this.calendarDatesChanged.emit({ current, start, end, formattedRange });
  }

  /**
   * Mark a break as active or not
   *
   * Being active, a break should be highlighted
   */
  toggleEventActiveClass(eventElement: Element, force?: boolean): void {
    eventElement.classList.toggle('fc-event-extended-active', force);
  }

  /**
   * Handle closing of opened popovers without a backdrop for UX reasons
   *
   * - Having a visible backdrop would imply more clicks for users (to close the backdrop in the first place)
   * - Having a transparent backdrop would be even worse (more clicks, plus users cannot expect this behavior,
   * because the backdrop is invisible)
   */
  @HostListener('document:mouseup', ['$event'])
  closeOpenedPopoversOnOutsideClick(event: MouseEvent): void {
    if (
      this.popoverDialogRef &&
      !(event.target as HTMLElement).closest('.mat-dialog-container')
    ) {
      this.closeAllDialogs();
    }
  }

  /**
   * Default event/slot for creation
   *
   * Starts on next hour when today, on midnight otherwise
   * Lasts 1 hour
   */
  defaultCreationEvent(): Partial<EventApi> {
    const calendarDate = moment(this.calendarApi.getDate());
    const isToday = moment().isSame(calendarDate, 'day');
    const start = (
      isToday
        ? calendarDate.clone().add(1, 'hour').startOf('hour')
        : calendarDate.clone().startOf('day')
    ).toDate();
    const end = moment(start).add(1, 'hour').toDate();

    return {
      start,
      end,
    };
  }

  /**
   * Delete an event/break
   */
  breakDeletion(event: EventApi): void {
    this.closeAllDialogs();

    this.dialog.open(BreakDeletionComponent, {
      data: event,
      closeOnNavigation: true,
    });
  }

  /**
   * Create an event/break
   */
  breakCreation(partialEvent: Partial<EventApi> = this.defaultCreationEvent()): void {
    this.closeAllDialogs();

    aLogEvent(AMPLITUDE_EVENT.BREAKS.NEW);
    this.dialog.open(BreakEditionComponent, {
      data: partialEvent,
      closeOnNavigation: true,
    });
  }

  /**
   * Edit an event/break
   */
  breakEdition(event: EventApi): void {
    this.closeAllDialogs();

    this.dialog.open(BreakEditionComponent, {
      data: event,
      closeOnNavigation: true,
    });
  }

  /**
   * Close all dialogs
   */
  closeAllDialogs(): void {
    this.dialog.closeAll();
  }

  addBreak(): void {
    // If no programs or playlists, show a tooltip
    if (
      !(
        this.store.selectSnapshot(SchedulerCoreState.hasNotEmptyPlaylists) ||
        this.store.selectSnapshot(ProgramsState.hasNotEmptyPrograms) ||
        this.store.selectSnapshot(SchedulerCoreState.allTracks).length > 0
      )
    ) {
      this.tooltipNoContent();
      return;
    }

    this.dialog.open(BreakEditionComponent, {
      ...MOBILE_DIALOG_CONFIG,
      data: {
        mobile: true,
        break: null,
      },
      panelClass: 'full-mobile-dialog',
    });
  }

  scrolled(tinyHeader: boolean): void {
    this.$tinyHeader.set(tinyHeader);
  }

  /**
   * Retrieve all programs of radio
   */
  @Dispatch()
  getPrograms(): ProgramsRequest {
    return new ProgramsRequest();
  }

  /**
   * Retrieve all playlists of radio
   */
  @Dispatch()
  getPlaylists(radioId: number): SchedulerAllPlaylistsRequest {
    return new SchedulerAllPlaylistsRequest(radioId);
  }

  /**
   * Retrieve all tracks of radio
   */
  @Dispatch()
  getTracks(radioId: number): SchedulerAllTracksRequest {
    return new SchedulerAllTracksRequest(radioId);
  }

  @Dispatch()
  getBreakList = (from: moment.Moment, to: moment.Moment) => {
    this.calendarApi.removeAllEvents();
    return new GetBreakListRequest({ from, to });
  };

  @Dispatch()
  resetState = () => new ResetBreakState();

  @Dispatch()
  setCurrentDay(day: moment.Moment) {
    return new SetCurrentDay(day);
  }

  @Dispatch()
  tooltipNoContent = () => new TooltipNoContent();
}
